import type { FeedItemPhoto } from '@/types/feed';
import { imageUrl } from '@/utils/image-url';
import { useEffect, useState } from 'react';
import { Image, Pressable, View } from 'react-native';

type Orientation = 'portrait' | 'landscape';

interface PhotoGridProps {
  photos: FeedItemPhoto[];
  onPhotoPress: (index: number) => void;
}

function usePhotoOrientations(photos: FeedItemPhoto[]): Orientation[] | null {
  const [orientations, setOrientations] = useState<Orientation[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    const uris = photos.map((p) => imageUrl(p.r2Key || p.imageUrl, 'medium'));

    Promise.all(
      uris.map(
        (uri) =>
          new Promise<Orientation>((resolve) => {
            Image.getSize(
              uri,
              (w, h) => resolve(h > w ? 'portrait' : 'landscape'),
              () => resolve('landscape'),
            );
          }),
      ),
    ).then((results) => {
      if (!cancelled) setOrientations(results);
    });

    return () => {
      cancelled = true;
    };
  }, [photos]);

  return orientations;
}

function SinglePhoto({
  photo,
  onPress,
}: {
  photo: FeedItemPhoto;
  onPress: () => void;
}) {
  const [isPortrait, setIsPortrait] = useState(false);
  const uri = imageUrl(photo.r2Key || photo.imageUrl, 'medium');

  useEffect(() => {
    Image.getSize(uri, (w, h) => {
      setIsPortrait(h > w);
    });
  }, [uri]);

  return (
    <Pressable
      onPress={onPress}
      className={`overflow-hidden ${isPortrait ? 'aspect-[3/4]' : 'aspect-[4/3]'}`}
      accessibilityLabel="View check-in photo 1"
    >
      <Image
        source={{ uri }}
        className="w-full h-full"
        resizeMode="cover"
        accessible={false}
      />
    </Pressable>
  );
}

// -- 2-photo layouts --

function TwoLandscape({
  photos,
  onPhotoPress,
}: {
  photos: FeedItemPhoto[];
  onPhotoPress: (index: number) => void;
}) {
  return (
    <View className="flex-row h-48 gap-1 overflow-hidden">
      {photos.map((photo, index) => (
        <Pressable
          key={photo.id}
          onPress={() => onPhotoPress(index)}
          className="flex-1"
          accessibilityLabel={`View check-in photo ${index + 1}`}
        >
          <Image
            source={{ uri: imageUrl(photo.r2Key || photo.imageUrl, 'medium') }}
            className="w-full h-full"
            resizeMode="cover"
            accessible={false}
          />
        </Pressable>
      ))}
    </View>
  );
}

function TwoWithPortrait({
  photos,
  orientations,
  onPhotoPress,
}: {
  photos: FeedItemPhoto[];
  orientations: Orientation[];
  onPhotoPress: (index: number) => void;
}) {
  return (
    <View className="flex-row gap-1 overflow-hidden">
      {photos.map((photo, index) => (
        <Pressable
          key={photo.id}
          onPress={() => onPhotoPress(index)}
          className={`flex-1 ${orientations[index] === 'portrait' ? 'aspect-[3/4]' : ''}`}
          accessibilityLabel={`View check-in photo ${index + 1}`}
        >
          <Image
            source={{ uri: imageUrl(photo.r2Key || photo.imageUrl, 'medium') }}
            className="w-full h-full"
            resizeMode="cover"
            accessible={false}
          />
        </Pressable>
      ))}
    </View>
  );
}

// -- 3-photo layouts --

function ThreeLandscape({
  photos,
  onPhotoPress,
}: {
  photos: FeedItemPhoto[];
  onPhotoPress: (index: number) => void;
}) {
  return (
    <View className="flex-row h-48 gap-1 overflow-hidden">
      <Pressable
        onPress={() => onPhotoPress(0)}
        className="flex-1"
        accessibilityLabel="View check-in photo 1"
      >
        <Image
          source={{
            uri: imageUrl(photos[0].r2Key || photos[0].imageUrl, 'medium'),
          }}
          className="w-full h-full"
          resizeMode="cover"
          accessible={false}
        />
      </Pressable>
      <View className="flex-1 gap-1">
        <Pressable
          onPress={() => onPhotoPress(1)}
          className="flex-1"
          accessibilityLabel="View check-in photo 2"
        >
          <Image
            source={{
              uri: imageUrl(photos[1].r2Key || photos[1].imageUrl, 'medium'),
            }}
            className="w-full h-full"
            resizeMode="cover"
            accessible={false}
          />
        </Pressable>
        <Pressable
          onPress={() => onPhotoPress(2)}
          className="flex-1"
          accessibilityLabel="View check-in photo 3"
        >
          <Image
            source={{
              uri: imageUrl(photos[2].r2Key || photos[2].imageUrl, 'medium'),
            }}
            className="w-full h-full"
            resizeMode="cover"
            accessible={false}
          />
        </Pressable>
      </View>
    </View>
  );
}

function AllHorizontalRow({
  photos,
  orientations,
  onPhotoPress,
}: {
  photos: FeedItemPhoto[];
  orientations: Orientation[];
  onPhotoPress: (index: number) => void;
}) {
  return (
    <View className="flex-row gap-1 overflow-hidden">
      {photos.map((photo, index) => (
        <Pressable
          key={photo.id}
          onPress={() => onPhotoPress(index)}
          className={`flex-1 ${orientations[index] === 'portrait' ? 'aspect-[3/4]' : ''}`}
          accessibilityLabel={`View check-in photo ${index + 1}`}
        >
          <Image
            source={{ uri: imageUrl(photo.r2Key || photo.imageUrl, 'medium') }}
            className="w-full h-full"
            resizeMode="cover"
            accessible={false}
          />
        </Pressable>
      ))}
    </View>
  );
}

function PortraitPlusStackedLandscapes({
  photos,
  orientations,
  onPhotoPress,
}: {
  photos: FeedItemPhoto[];
  orientations: Orientation[];
  onPhotoPress: (index: number) => void;
}) {
  const portraitIdx = orientations.findIndex((o) => o === 'portrait');
  const landscapeIdxs = orientations
    .map((o, i) => (o === 'landscape' ? i : -1))
    .filter((i) => i >= 0);

  const portraitPhoto = photos[portraitIdx];
  const landscape1 = photos[landscapeIdxs[0]];
  const landscape2 = photos[landscapeIdxs[1]];

  return (
    <View className="flex-row gap-1 overflow-hidden">
      <Pressable
        onPress={() => onPhotoPress(portraitIdx)}
        className="flex-1 aspect-[3/4]"
        accessibilityLabel={`View check-in photo ${portraitIdx + 1}`}
      >
        <Image
          source={{
            uri: imageUrl(
              portraitPhoto.r2Key || portraitPhoto.imageUrl,
              'medium',
            ),
          }}
          className="w-full h-full"
          resizeMode="cover"
          accessible={false}
        />
      </Pressable>
      <View className="flex-1 gap-1">
        <Pressable
          onPress={() => onPhotoPress(landscapeIdxs[0])}
          className="flex-1"
          accessibilityLabel={`View check-in photo ${landscapeIdxs[0] + 1}`}
        >
          <Image
            source={{
              uri: imageUrl(
                landscape1.r2Key || landscape1.imageUrl,
                'medium',
              ),
            }}
            className="w-full h-full"
            resizeMode="cover"
            accessible={false}
          />
        </Pressable>
        <Pressable
          onPress={() => onPhotoPress(landscapeIdxs[1])}
          className="flex-1"
          accessibilityLabel={`View check-in photo ${landscapeIdxs[1] + 1}`}
        >
          <Image
            source={{
              uri: imageUrl(
                landscape2.r2Key || landscape2.imageUrl,
                'medium',
              ),
            }}
            className="w-full h-full"
            resizeMode="cover"
            accessible={false}
          />
        </Pressable>
      </View>
    </View>
  );
}

export function PhotoGrid({ photos, onPhotoPress }: PhotoGridProps) {
  const orientations = usePhotoOrientations(photos);

  if (photos.length === 0) return null;

  if (photos.length === 1) {
    return <SinglePhoto photo={photos[0]} onPress={() => onPhotoPress(0)} />;
  }
  const portraitCount =
    orientations?.filter((o) => o === 'portrait').length ?? 0;

  if (photos.length === 2) {
    if (portraitCount === 0 || !orientations) {
      return <TwoLandscape photos={photos} onPhotoPress={onPhotoPress} />;
    }
    return (
      <TwoWithPortrait
        photos={photos}
        orientations={orientations}
        onPhotoPress={onPhotoPress}
      />
    );
  }

  // 3+ photos
  if (portraitCount === 0 || !orientations) {
    return <ThreeLandscape photos={photos} onPhotoPress={onPhotoPress} />;
  }
  if (portraitCount >= 2) {
    return (
      <AllHorizontalRow
        photos={photos}
        orientations={orientations}
        onPhotoPress={onPhotoPress}
      />
    );
  }
  return (
    <PortraitPlusStackedLandscapes
      photos={photos}
      orientations={orientations}
      onPhotoPress={onPhotoPress}
    />
  );
}
